/**
 * Position management functionality for Alpaca API
 */

import { makeTradeRequest } from './client';
import { getAccount } from './account';

/**
 * Position interfaces
 */
export interface Position {
  asset_id: string;
  symbol: string;
  exchange: string;
  asset_class: string;
  avg_entry_price: string;
  qty: string;
  side: 'long' | 'short';
  market_value: string;
  cost_basis: string;
  unrealized_pl: string;
  unrealized_plpc: string;
  unrealized_intraday_pl: string;
  unrealized_intraday_plpc: string;
  current_price: string;
  lastday_price: string;
  change_today: string;
}

/**
 * Get all open positions
 */
export async function getPositions(): Promise<Position[]> {
  return makeTradeRequest<Position[]>('GET', '/positions');
}

/**
 * Get an open position for a specific symbol
 */
export async function getPosition(symbol: string): Promise<Position> {
  return makeTradeRequest<Position>('GET', `/positions/${symbol}`);
}

/**
 * Close a position for a specific symbol
 */
export async function closePosition(symbol: string, percentage: number = 100): Promise<any> {
  const params = percentage === 100 ? undefined : { percentage: percentage.toString() };
  return makeTradeRequest<any>('DELETE', `/positions/${symbol}`, params);
}

/**
 * Close all positions
 */
export async function closeAllPositions(): Promise<Position[]> {
  return makeTradeRequest<Position[]>('DELETE', '/positions');
}

/**
 * Calculate position size based on risk percentage
 * @param symbol - The stock symbol
 * @param entryPrice - The entry price per share
 * @param stopPrice - The stop loss price per share
 * @param riskPercentage - The percentage of account equity to risk (e.g., 1 for 1%)
 * @returns The number of shares to purchase
 */
export async function calculatePositionSizeByRisk(
  symbol: string,
  entryPrice: number,
  stopPrice: number,
  riskPercentage: number
): Promise<number> {
  if (entryPrice <= 0 || stopPrice <= 0 || riskPercentage <= 0) {
    throw new Error('Invalid parameters for position sizing');
  }
  
  // Get account information to determine risk amount
  const account = await getAccount();
  const equity = parseFloat(account.equity);
  
  // Calculate risk amount based on account equity and risk percentage
  const riskAmount = equity * (riskPercentage / 100);
  
  // Calculate risk per share
  const riskPerShare = Math.abs(entryPrice - stopPrice);
  
  if (riskPerShare === 0) {
    throw new Error('Stop price cannot be equal to entry price');
  }
  
  // Calculate position size (number of shares)
  const shares = Math.floor(riskAmount / riskPerShare);
  
  return shares;
}

/**
 * Calculate position size based on percentage of buying power
 * @param percentage - The percentage of buying power to use (e.g., 10 for 10%)
 * @param price - The current price per share
 * @returns The number of shares to purchase
 */
export async function calculatePositionSizeByBuyingPower(
  percentage: number,
  price: number
): Promise<number> {
  if (percentage <= 0 || price <= 0) {
    throw new Error('Invalid parameters for position sizing');
  }
  
  // Get account information to determine buying power
  const account = await getAccount();
  const buyingPower = parseFloat(account.buying_power);
  
  // Calculate amount to invest
  const investAmount = buyingPower * (percentage / 100);
  
  // Calculate number of shares
  const shares = Math.floor(investAmount / price);
  
  return shares;
}